import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import clsx from 'clsx';

import { LoadingSpinner } from '../../../components/LoadingSpinner';
import { useToastStore } from '../../../stores/toastStore';
import { addDocumentComment, type DocumentCommentResponse } from '../api';
import { reviewerQueryKeys } from '../hooks/useDocumentResult';

interface CommentsPanelProps {
  open: boolean;
  onClose: () => void;
  jobId: string;
  comments: ReadonlyArray<DocumentCommentResponse>;
  isLoading: boolean;
  /** Logged-in user's id, to render own comments as "Tú". */
  currentUserId: string | null;
}

const timeFormatter = new Intl.DateTimeFormat('es-ES', { dateStyle: 'short', timeStyle: 'short' });

function formatInstant(iso: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : timeFormatter.format(date);
}

/**
 * Slide-in panel with the reviewer comment thread of a document (J2). Lists comments oldest-first and
 * lets the reviewer post a new one. Comments are free-text collaboration content, not document data.
 */
export function CommentsPanel({
  open,
  onClose,
  jobId,
  comments,
  isLoading,
  currentUserId,
}: CommentsPanelProps): JSX.Element {
  const queryClient = useQueryClient();
  const pushToast = useToastStore((s) => s.push);
  const [body, setBody] = useState('');

  const mutation = useMutation({
    mutationFn: (text: string) => addDocumentComment(jobId, text),
    onSuccess: () => {
      setBody('');
      void queryClient.invalidateQueries({ queryKey: reviewerQueryKeys.comments(jobId) });
    },
    onError: (e: unknown) =>
      pushToast({ tone: 'error', title: 'No se pudo publicar el comentario', description: e instanceof Error ? e.message : '' }),
  });

  const trimmed = body.trim();
  const canSubmit = trimmed !== '' && !mutation.isPending;

  return (
    <aside
      aria-hidden={!open}
      className={clsx(
        'fixed inset-y-0 right-0 z-30 flex w-full max-w-sm transform flex-col border-l border-slate-200 bg-white shadow-xl transition-transform duration-300',
        open ? 'translate-x-0' : 'translate-x-full',
      )}
      data-testid="comments-panel"
    >
      <header className="flex items-center justify-between border-b border-slate-200 px-4 py-3">
        <h2 className="text-sm font-semibold text-slate-900">Comentarios</h2>
        <button
          type="button"
          onClick={onClose}
          className="rounded p-1 text-slate-500 hover:bg-slate-100"
          aria-label="Cerrar comentarios"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-4 w-4">
            <path d="M18 6L6 18" />
            <path d="M6 6l12 12" />
          </svg>
        </button>
      </header>

      <div className="flex-1 min-h-0 space-y-3 overflow-y-auto p-4">
        {isLoading ? (
          <div className="flex justify-center py-6">
            <LoadingSpinner label="Cargando comentarios…" />
          </div>
        ) : comments.length === 0 ? (
          <p className="rounded-md border border-dashed border-slate-300 bg-slate-50 p-4 text-center text-xs text-slate-500">
            Sin comentarios todavía. Sé el primero en dejar una nota.
          </p>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="rounded-md border border-slate-200 p-3" data-testid="comment-item">
              <div className="flex items-center justify-between gap-2">
                <span className="truncate text-xs font-semibold text-slate-700">
                  {comment.authorUserId === currentUserId ? 'Tú' : comment.authorUserId}
                </span>
                <span className="flex-none text-[11px] text-slate-400">{formatInstant(comment.createdAt)}</span>
              </div>
              <p className="mt-1 whitespace-pre-wrap break-words text-sm text-slate-800">{comment.body}</p>
            </div>
          ))
        )}
      </div>

      <form
        className="border-t border-slate-200 p-3"
        onSubmit={(e) => {
          e.preventDefault();
          if (canSubmit) {
            mutation.mutate(trimmed);
          }
        }}
      >
        <textarea
          className="input min-h-[72px] resize-y text-sm"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Escribe un comentario…"
          maxLength={4000}
          data-testid="comment-input"
        />
        <button
          type="submit"
          className="btn-primary mt-2 w-full"
          disabled={!canSubmit}
          data-testid="comment-submit"
        >
          {mutation.isPending ? 'Publicando…' : 'Comentar'}
        </button>
      </form>
    </aside>
  );
}
